import type {TFunction, TOptions} from 'i18next'

import type {WithLocalizableName} from 'store/i18n'
import {prepareT as blankPrepareT, combineTOptions} from 'store/i18n'


const prepareT = (value: string, options?: TOptions|string) =>
  combineTOptions(blankPrepareT(value, options), {namespace: 'translation'})

interface LocalizedSelectOption<T = string> extends WithLocalizableName {
  readonly value: T
  readonly disabled?: true
}

type RealUnit = Exclude<bayes.bob.SalaryUnit, 'UNKNOWN_SALARY_UNIT'>
const SALARY_TO_GROSS_ANNUAL_FACTORS: Record<RealUnit, number> = {
  ANNUAL_GROSS_SALARY: 1,
  HOURLY_GROSS_SALARY: 52 * config.hoursPerWeek,
  HOURLY_NET_SALARY: 52 * config.hoursPerWeek / config.grossToNet,
  MONTHLY_GROSS_SALARY: 12,
  MONTHLY_NET_SALARY: 12 / config.grossToNet,
} as const


const convertToUnit = (salary: bayes.bob.SalaryEstimation, unit: RealUnit):
bayes.bob.SalaryEstimation => {
  const {maxSalary = 0, medianSalary = 0, minSalary = 0, unit: previousUnit} = salary
  if (!previousUnit || previousUnit === 'UNKNOWN_SALARY_UNIT' || previousUnit === unit) {
    // Unable/Useless to transfer to a new unit.
    return salary
  }
  const [newMin, newMed, newMax] = [minSalary, medianSalary, maxSalary].map(s =>
    s * SALARY_TO_GROSS_ANNUAL_FACTORS[previousUnit] / SALARY_TO_GROSS_ANNUAL_FACTORS[unit])
  return {
    maxSalary: newMax,
    medianSalary: newMed,
    minSalary: newMin,
    unit,
  }
}

const SALARY_UNIT_OPTIONS: readonly LocalizedSelectOption<RealUnit>[] = [
  {name: prepareT('brut par an'), value: 'ANNUAL_GROSS_SALARY'},
  {name: prepareT('net par mois'), value: 'MONTHLY_NET_SALARY'},
  {name: prepareT('brut par mois'), value: 'MONTHLY_GROSS_SALARY'},
  {name: prepareT('net par heure'), value: 'HOURLY_NET_SALARY'},
  {name: prepareT('brut par heure'), value: 'HOURLY_GROSS_SALARY'},
] as const

const getSalaryText = (unit: RealUnit, translate: TFunction): string => {
  const {name} = SALARY_UNIT_OPTIONS.find(({value}) => value === unit) || {}
  if (!name) {
    return ''
  }
  return translate(...name)
}

export {
  convertToUnit,
  getSalaryText,
}
